import React, { useEffect, useMemo, useState } from 'react';
import api from '../config/axios';

function getFirmHeaders(firmSlug) {
  return {
    'x-firm-slug': firmSlug
  };
}

function safeJsonParse(input, fallback) {
  try {
    return JSON.parse(input);
  } catch {
    return fallback;
  }
}

function normalizeUrlInput(v) {
  const s = String(v || '').trim();
  return s === '' ? '' : s;
}

function uniqueDays(days) {
  const out = Array.from(new Set((days || []).map((d) => Number(d)).filter((n) => Number.isFinite(n))));
  out.sort((a, b) => a - b);
  return out;
}

const DAY_LABELS = [
  { v: 1, t: 'Pzt' },
  { v: 2, t: 'Sal' },
  { v: 3, t: 'Çar' },
  { v: 4, t: 'Per' },
  { v: 5, t: 'Cum' },
  { v: 6, t: 'Cts' },
  { v: 7, t: 'Paz' }
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0') + ':00');

export default function FirmSettingsPanel({ firmSlug }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [sendOnlyInBusinessHours, setSendOnlyInBusinessHours] = useState(false);
  const [timezone, setTimezone] = useState('Europe/Istanbul');
  const [cartCustomUrl, setCartCustomUrl] = useState('');

  const [businessHours, setBusinessHours] = useState({
    start: '09:00',
    end: '18:00',
    days: [1, 2, 3, 4, 5],
    timezone: 'Europe/Istanbul'
  });

  const effectiveBusinessHours = useMemo(() => {
    return {
      ...businessHours,
      days: uniqueDays(businessHours.days),
      timezone
    };
  }, [businessHours, timezone]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!firmSlug) return;
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const res = await api.get(`/firms/${firmSlug}/settings`, {
          headers: getFirmHeaders(firmSlug)
        });
        const data = res?.data?.data;

        if (cancelled) return;

        setModuleEnabled(Boolean(data?.module_enabled));
        setSendOnlyInBusinessHours(Boolean(data?.send_only_in_business_hours));
        setTimezone(String(data?.timezone || 'Europe/Istanbul'));
        setCartCustomUrl(normalizeUrlInput(data?.cart_custom_url));

        const parsed = safeJsonParse(data?.business_hours_json, null);
        if (parsed && typeof parsed === 'object') {
          setBusinessHours({
            start: parsed.start || '09:00',
            end: parsed.end || '18:00',
            days: Array.isArray(parsed.days) ? parsed.days : [1, 2, 3, 4, 5],
            timezone: parsed.timezone || String(data?.timezone || 'Europe/Istanbul')
          });
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.error || e?.message || 'Ayarlar yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [firmSlug]);

  function toggleDay(day) {
    setBusinessHours((prev) => {
      const days = uniqueDays(prev.days);
      if (days.includes(day)) {
        return { ...prev, days: days.filter((d) => d !== day) };
      }
      return { ...prev, days: uniqueDays([...days, day]) };
    });
  }

  async function save() {
    if (!firmSlug) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        module_enabled: moduleEnabled,
        send_only_in_business_hours: sendOnlyInBusinessHours,
        timezone,
        cart_custom_url: cartCustomUrl.trim() || null,
        business_hours: sendOnlyInBusinessHours ? effectiveBusinessHours : null
      };

      const res = await api.put(`/firms/${firmSlug}/settings`, payload, {
        headers: getFirmHeaders(firmSlug)
      });

      if (res?.data?.success) {
        setSuccess('Kaydedildi');
      } else {
        setError(res?.data?.error || 'Kaydetme başarısız');
      }
    } catch (e) {
      setError(e?.error || e?.message || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  }

  if (!firmSlug) {
    return (
      <div style={{ color: '#666' }}>
        Firma seçilemedi.
      </div>
    );
  }

  return (
    <div>
      {(error || success) && (
        <div style={{ marginBottom: '15px' }}>
          {error && (
            <div style={{ background: '#f8d7da', color: '#721c24', padding: '12px 14px', borderRadius: '10px' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ background: '#d4edda', color: '#155724', padding: '12px 14px', borderRadius: '10px' }}>
              {success}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        <div style={{ border: '1px solid #e9ecef', borderRadius: '12px', padding: '16px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Genel</h3>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={moduleEnabled}
              onChange={(e) => setModuleEnabled(e.target.checked)}
              disabled={loading || saving}
            />
            <span style={{ fontWeight: 600 }}>Modül Aktif</span>
          </label>

          <div style={{ height: '10px' }} />

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={sendOnlyInBusinessHours}
              onChange={(e) => setSendOnlyInBusinessHours(e.target.checked)}
              disabled={loading || saving}
            />
            <span style={{ fontWeight: 600 }}>Sadece mesai saatlerinde gönder</span>
          </label>

          <div style={{ height: '10px' }} />

          <div style={{ display: 'grid', gap: '8px' }}>
            <div style={{ fontWeight: 600 }}>Saat Dilimi</div>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={loading || saving}
              style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef' }}
            >
              <option value="Europe/Istanbul">Europe/Istanbul</option>
            </select>
          </div>
        </div>

        <div style={{ border: '1px solid #e9ecef', borderRadius: '12px', padding: '16px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Sepet Linki</h3>
          <div style={{ color: '#666', fontSize: '13px', marginBottom: '10px' }}>
            Boş bırakırsan sistem varsayılan sepet linkini kullanır.
          </div>

          <input
            type="url"
            placeholder="https://site.com/index.php?route=checkout/cart"
            value={cartCustomUrl}
            onChange={(e) => setCartCustomUrl(e.target.value)}
            disabled={loading || saving}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef' }}
          />
        </div>

        <div style={{ border: '1px solid #e9ecef', borderRadius: '12px', padding: '16px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Mesai Saatleri</h3>

          {!sendOnlyInBusinessHours && (
            <div style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '10px', color: '#666', fontSize: '13px' }}>
              Mesai filtresi kapalı. Açarsan aşağıdaki aralığa göre gönderim kısıtlanır.
            </div>
          )}

          <div style={{ height: '10px' }} />

          <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>Başlangıç</div>
              <select
                value={businessHours.start}
                onChange={(e) => setBusinessHours((p) => ({ ...p, start: e.target.value }))}
                disabled={loading || saving || !sendOnlyInBusinessHours}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef' }}
              >
                {HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>Bitiş</div>
              <select
                value={businessHours.end}
                onChange={(e) => setBusinessHours((p) => ({ ...p, end: e.target.value }))}
                disabled={loading || saving || !sendOnlyInBusinessHours}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef' }}
              >
                {HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ height: '12px' }} />

          <div style={{ fontWeight: 600, marginBottom: '8px' }}>Günler</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {DAY_LABELS.map((d) => {
              const active = uniqueDays(businessHours.days).includes(d.v);
              return (
                <button
                  key={d.v}
                  type="button"
                  onClick={() => toggleDay(d.v)}
                  disabled={loading || saving || !sendOnlyInBusinessHours}
                  style={{
                    border: '1px solid #e9ecef',
                    background: active ? '#007bff' : 'white',
                    color: active ? 'white' : '#333',
                    padding: '8px 10px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                >
                  {d.t}
                </button>
              );
            })}
          </div>

          {sendOnlyInBusinessHours && uniqueDays(businessHours.days).length === 0 && (
            <div style={{ marginTop: '10px', color: '#721c24', fontSize: '13px' }}>
              En az 1 gün seçmelisin.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={save}
            disabled={loading || saving || (sendOnlyInBusinessHours && uniqueDays(businessHours.days).length === 0)}
            style={{
              background: saving ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '10px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 700
            }}
          >
            {loading ? 'Yükleniyor...' : saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
